import type {
  CategoryMeta,
  CategoryTag,
  StoreItems,
  StoreItemsByCategory,
} from '../types/get-store-items';

/**
 * Categoria já resolvida para renderização no cardápio, combinando os itens
 * com o metadado de ordenação/virtualização vindo do backend.
 */
export type ResolvedCategory = {
  name: string; // nome exibido da categoria
  items: StoreItems; // produtos da categoria (já filtrados pela busca)
  isVirtual: boolean; // true para categorias virtuais (dinâmicas)
  tag: CategoryTag | null; // etiqueta saneada, ou null quando não há
};

// Etiqueta padrão usada como fallback quando uma categoria virtual não traz
// um rótulo válido do backend.
export const DEFAULT_VIRTUAL_TAG_LABEL = 'Destaques';

// Limite defensivo de tamanho do rótulo da etiqueta para evitar quebra de
// layout e abuso de conteúdo controlado pelo backend.
const MAX_TAG_LABEL_LENGTH = 40;

// Aceita apenas cores hexadecimais (#RGB, #RRGGBB, #RRGGBBAA). Qualquer outro
// valor é descartado para impedir injeção de CSS via style inline (OWASP A03).
const SAFE_HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/**
 * Retorna a cor apenas se for um hex seguro; caso contrário, `null`.
 * Impede que valores arbitrários cheguem a `style={{ color/background }}`.
 */
export function sanitizeTagColor(color?: string | null): string | null {
  if (typeof color !== 'string') return null;
  const trimmed = color.trim();
  return SAFE_HEX_COLOR.test(trimmed) ? trimmed : null;
}

/**
 * Sanea a etiqueta de uma categoria: valida o tipo, remove espaços,
 * limita o tamanho e valida a cor. Aplica o rótulo de fallback quando
 * a categoria é virtual e não há rótulo utilizável.
 */
function sanitizeTag(tag: CategoryTag | null | undefined, isVirtual: boolean): CategoryTag | null {
  const rawLabel = typeof tag?.label === 'string' ? tag.label.trim() : '';
  const label = rawLabel.slice(0, MAX_TAG_LABEL_LENGTH);

  if (!label) {
    // Sem rótulo válido: categorias virtuais recebem o fallback; comuns ficam sem etiqueta.
    return isVirtual ? { label: DEFAULT_VIRTUAL_TAG_LABEL, color: null } : null;
  }

  return { label, color: sanitizeTagColor(tag?.color) };
}

/**
 * Combina os itens do cardápio com os metadados opcionais de categoria,
 * produzindo a lista ordenada pronta para renderização.
 *
 * Regras de fallback:
 * - Sem `categories` (backend antigo/ausente): usa a ordem natural de `items`.
 * - Categorias presentes no metadado seguem a ordem informada.
 * - Categorias em `items` sem metadado são anexadas ao final (preservando ordem).
 * - Categorias virtuais sem itens visíveis (ex.: filtradas pela busca) são omitidas.
 */
export function resolveMenuCategories(
  items: StoreItemsByCategory,
  categories?: CategoryMeta[]
): ResolvedCategory[] {
  const safeItems = items ?? {};

  // Sem metadados válidos: fallback direto para a listagem simples.
  if (!Array.isArray(categories) || categories.length === 0) {
    return Object.entries(safeItems)
      .filter(([, categoryItems]) => Array.isArray(categoryItems) && categoryItems.length > 0)
      .map(([name, categoryItems]) => ({
        name,
        items: categoryItems,
        isVirtual: false,
        tag: null,
      }));
  }

  const resolved: ResolvedCategory[] = [];
  const consumed = new Set<string>();

  for (const meta of categories) {
    const name = typeof meta?.name === 'string' ? meta.name : '';
    if (!name || consumed.has(name)) continue;

    const categoryItems = safeItems[name];
    if (!Array.isArray(categoryItems) || categoryItems.length === 0) continue;

    consumed.add(name);
    const isVirtual = meta.is_virtual === true;
    resolved.push({
      name,
      items: categoryItems,
      isVirtual,
      tag: sanitizeTag(meta.tag, isVirtual),
    });
  }

  // Fallback: categorias presentes em `items` mas ausentes do metadado.
  for (const [name, categoryItems] of Object.entries(safeItems)) {
    if (consumed.has(name)) continue;
    if (!Array.isArray(categoryItems) || categoryItems.length === 0) continue;
    resolved.push({ name, items: categoryItems, isVirtual: false, tag: null });
  }

  return resolved;
}

/** Nome da categoria virtual que separa os combos do cardápio (PRAT-79). */
export const COMBOS_CATEGORY_NAME = 'Combos';

// Categorias automáticas da API (config/automatic_categories.php).
const AUTOMATIC_CATEGORY_NAMES = new Set(['Promoções', 'Mais vendidos', 'Novidades']);

/**
 * Acrescenta a categoria virtual "Combos" (PRAT-79): todos os combos (`type` 2)
 * do cardápio, sem repetir, logo depois das categorias virtuais do topo
 * (Promoções, Mais vendidos…). Não aparece sem combo nem quando a loja já tem
 * uma categoria chamada "Combos".
 */
export function withCombosCategory(categories: ResolvedCategory[]): ResolvedCategory[] {
  const hasOwn = categories.some(
    category => category.name.trim().toLowerCase() === COMBOS_CATEGORY_NAME.toLowerCase()
  );
  if (hasOwn) return categories;

  const seen = new Set<string>();
  const combos: StoreItems = [];
  for (const category of categories) {
    for (const item of category.items) {
      if (Number(item?.type) !== 2 || seen.has(item.id)) continue;
      seen.add(item.id);
      combos.push(item);
    }
  }
  if (combos.length === 0) return categories;

  // Sem metadado do backend, as automáticas chegam como categoria comum: o
  // "Combos" entra depois delas pelo nome.
  const insertAt = categories.findIndex(
    category => !category.isVirtual && !AUTOMATIC_CATEGORY_NAMES.has(category.name)
  );
  const position = insertAt === -1 ? categories.length : insertAt;
  return [
    ...categories.slice(0, position),
    {
      name: COMBOS_CATEGORY_NAME,
      items: combos,
      isVirtual: true,
      tag: { label: COMBOS_CATEGORY_NAME, color: null },
    },
    ...categories.slice(position),
  ];
}
